"use client"

import { useEffect, useState } from "react"
import { Search, UserPlus, ChevronDown, Edit, Key, Trash2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { Avatar, AvatarImage } from "../components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu"
import { AddUserModal } from "../components/add-user-modal"
import { EditUserModal } from "../components/edit-user-modal"
import { ChangePasswordModal } from "../components/change-password-modal"
import { DeleteUserModal } from '../components/delete-user-modal'
import { userManagementService } from "../apis/userManagement";
import noprofile from "../asset/img/noprofile.webp"
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import toast from "react-hot-toast";
import { base64Logo } from '../constants/pdfLogo'

export default function UserManagementPage() {
  const [users, setUsers] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [entriesPerPage, setEntriesPerPage] = useState(5)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" })
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  // Modal states
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false)
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false)
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true)
      try {
        const [usersRes, rolesRes] = await Promise.all([
          userManagementService.getAllUsers(),
          userManagementService.getRoles(),
          userManagementService.getEnabledMailUsers()
        ]);
        if(usersRes.success && usersRes?.data?.body?.data) {
          const mappedUsers = usersRes.data.body.data.map(user => ({
            id: user.userid || user.id,
            name: user.name,
            email: user.emailid,
            phone: user.phoneno,
            role: user.role,
            lastLogin: user.lastLoginAgo || 'Never',
            mailStatus: user.mail_status === 'enabled' ? 'Enabled' : 'Disabled',
            profileImage: user?.profileimage || "https://cdn.pixabay.com/photo/2023/02/18/11/00/icon-7797704_640.png" || user?.profileImagePath || "/assets/noprofile.webp",
            // Keep original data for reference
            originalData: user
          }))
          setUsers(mappedUsers);
        } else {
          console.error("Users error:", usersRes?.message || "Failed to fetch users");
          setUsers([]);
        }

        if (rolesRes.success && rolesRes?.data?.body?.data) {
          setRoles(rolesRes.data.body.data);
        } else {
          console.error("Roles error:", rolesRes?.message || "Failed to fetch roles");
          setRoles([]);
        }

      } catch (error) {
        console.error("Error fetching data:", error);
        setUsers([]);
        setRoles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // Handle search with safe checks
  const filteredUsers = users.filter((user) => {
    if (!user) return false;
    const name = user.name || '';
    const email = user.email || '';
    const phone = user.phone || '';
    const role = user.role || '';

    return (
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })

  // Handle sorting with safe checks
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!sortConfig.key) return 0

    const aValue = a[sortConfig.key] || ''
    const bValue = b[sortConfig.key] || ''

    if (aValue < bValue) {
      return sortConfig.direction === "ascending" ? -1 : 1
    }
    if (aValue > bValue) {
      return sortConfig.direction === "ascending" ? 1 : -1
    }
    return 0
  })

  // Handle pagination
  const indexOfLastEntry = currentPage * entriesPerPage
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage
  const currentEntries = sortedUsers.slice(indexOfFirstEntry, indexOfLastEntry)
  const totalPages = Math.ceil(sortedUsers.length / entriesPerPage)

  // Request sort
  const requestSort = (key) => {
    let direction = "ascending"
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending"
    }
    setSortConfig({ key, direction })
  }

  const handleEmailStatusChange = async (userId, newStatus) => {
    try {
      // Find the user to get their profile ID
      const user = users.find(u => u.id === userId);
      if (!user) {
        console.error("User not found");
        return;
      }
  
      // Store original status for potential revert
      const originalStatus = user.mailStatus;
  
      // Update local state immediately for better UX
      setUsers(users.map(user =>
        user.id === userId
          ? { ...user, mailStatus: newStatus }
          : user
      ));
  
      // Call API to update the status using profile ID
      const result = await userManagementService.updateMailStatus(user.id, newStatus);
  
      // Check the nested response structure
      const apiResponse = result?.data?.body;
      const isSuccess = result?.success && apiResponse?.message;
  
      if (!isSuccess) {
        // Revert the change if API call fails
        setUsers(users.map(user =>
          user.id === userId
            ? { ...user, mailStatus: originalStatus }
            : user
        ));
        
        const errorMessage = apiResponse?.message || result?.message || 'Unknown error occurred';
        toast.error(`Failed to update email status: ${errorMessage}`);
      } else {
        // Show success message
        toast.success(apiResponse.message || 'Email status updated successfully');
      }
    } catch (error) {
      console.error("Error updating email status:", error);
      
      // Find original status for revert
      const originalUser = users.find(u => u.id === userId);
      const originalStatus = originalUser?.mailStatus === newStatus 
        ? (newStatus === 'Enabled' ? 'Disabled' : 'Enabled') 
        : originalUser?.mailStatus;
      
      // Revert the change if API call fails
      setUsers(users.map(user =>
        user.id === userId
          ? { ...user, mailStatus: originalStatus }
          : user
      ));
      
      toast.error(`Error updating email status: ${error.message || 'Network error occurred'}`);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ["Name", "Email", "Phone", "Role", "Last Login", "Mail Status"]
    const csvData = [
      headers.join(","),
      ...filteredUsers.map((user) =>
        [
          user.name || '',
          user.email || '',
          user.phone || '',
          user.role || '',
          user.lastLogin || '',
          user.mailStatus || ''
        ].join(","),
      ),
    ].join("\n")

    const blob = new Blob([csvData], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "user_management.csv"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();

      // Set document properties
      doc.setProperties({
        title: 'User Management Report',
        subject: 'User Management Data',
        author: 'Elite Poker Lounge',
        creator: 'User Management System'
      });

      // Add the logo (base64 image)
      const logoWidth = 40; // adjust width as needed
      const logoHeight = 20; // adjust height as needed
      doc.addImage(base64Logo, 'PNG', 150, 10, logoWidth, logoHeight); // x, y, width, height

      // Add timestamp
      const now = new Date();
      const dateTime = now.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });

      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Date & Time: ${dateTime}`, 14, 15);

      // Title
      doc.setFontSize(18);
      doc.setTextColor(0);
      doc.setFont(undefined, 'bold');
      doc.text('User Management', 14, 30);

      // Prepare table data
      const tableHeaders = ['Name', 'Email', 'Phone', 'Role', 'Last Login'];
      const tableData = filteredUsers.map(user => [
        user.name || 'N/A',
        user.email || 'N/A',
        user.phone || 'N/A',
        user.role || 'N/A',
        user.lastLogin || 'Never'
      ]);

      // Add table
      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: 40,
        theme: 'plain',
        styles: {
          fontSize: 9,
          cellPadding: 3,
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1
        },
        headStyles: {
          fillColor: false,
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineWidth: 0.1,
          lineColor: [0, 0, 0]
        },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { cellWidth: 65 },
          2: { cellWidth: 25 },
          3: { cellWidth: 30 },
          4: { cellWidth: 25 }
        },
        margin: { left: 14, right: 14 },
        tableLineColor: [0, 0, 0],
        tableLineWidth: 0.1
      });

      // Save the PDF
      const fileName = `User_Management_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF. Please try again.');
    }
  };

  // Delete user
  const confirmDeleteUser = (id) => {
    const user = users.find((user) => user.id === id)
    setSelectedUser(user)
    setIsDeleteDialogOpen(true)
  }

  const deleteUser = async () => {
    try {
      // Call API to delete the user
      const result = await userManagementService.deleteUser(selectedUser.id);

      if (result.success) {
        setUsers(users.filter((user) => user.id !== selectedUser.id))
        setIsDeleteDialogOpen(false)
        toast.success(`User ${selectedUser.id} deleted successfully`)
      } else {
        toast.error(`Failed to delete user: ${result.message}`);
      }
    } catch (error) {
      console.error("Error deleting user:", error)
      toast.error(`Error deleting user: ${error.message}`);
    }
  }

  // Edit user
  const editUser = (id) => {
    const user = users.find((user) => user.id === id)
    setSelectedUser(user)
    setIsEditUserModalOpen(true)
  }

  // Change password
  const changePassword = (id) => {
    setSelectedUser(users.find((user) => user.id === id))
    setIsChangePasswordModalOpen(true)
  }

  // Add user
  const addUser = () => {
    setIsAddUserModalOpen(true)
  }

  // Handle add user
  const handleAddUser = async (newUserData) => {
    try {
      const result = await userManagementService.addUser(newUserData);
    
      if (result.success) {
        // Refresh the users list to get the updated data
        const usersRes = await userManagementService.getAllUsers();
        if (usersRes.success && usersRes?.data?.body?.data) {
          const mappedUsers = usersRes.data.body.data.map(user => ({
            id: user.userid || user.id,
            name: user.name,
            email: user.emailid,
            phone: user.phoneno,
            role: user.role,
            lastLogin: user.lastLoginAgo || 'Never',
            mailStatus: user.mail_status === 'enabled' ? 'Enabled' : 'Disabled',
            profileImage: user?.profileimage || "https://cdn.pixabay.com/photo/2023/02/18/11/00/icon-7797704_640.png" || user?.profileImagePath || "/assets/noprofile.webp",
            originalData: user
          }));
          setUsers(mappedUsers);
        }
        toast.success('User added successfully!');
      } else {
        const errorMessage = result?.originalError?.response?.data?.body?.message || result.message;
        toast.error(`${errorMessage}`);
      }
    }catch (error) {
      console.error("Error adding user:", error);
      const errorMessage = error?.response?.data?.body?.message || error.message;
      toast.error(`${errorMessage}`);
    }
    
  }

  // Handle update user
  const handleUpdateUser = async (updatedUserData) => {
    try {
      const result = await userManagementService.updateUser(updatedUserData.id, updatedUserData);

      if (result.success) {
        // Refresh the users list to get the updated data
        const usersRes = await userManagementService.getAllUsers();
        if (usersRes.success && usersRes?.data?.body?.data) {
          const mappedUsers = usersRes.data.body.data.map(user => ({
            id: user.userid || user.id,
            name: user.name,
            email: user.emailid,
            phone: user.phoneno,
            role: user.role,
            lastLogin: user.lastLoginAgo || 'Never',
            mailStatus: user.mail_status === 'enabled' ? 'Enabled' : 'Disabled',
            profileImage: user?.profileimage || "https://cdn.pixabay.com/photo/2023/02/18/11/00/icon-7797704_640.png" || user?.profileImagePath || "/assets/noprofile.webp",
            originalData: user
          }));
          setUsers(mappedUsers);
        }
        toast.success('User updated successfully!');
      } else {
        toast.error(`Failed to update user: ${result.message}`);
      }
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(`Error updating user: ${error.message}`);
    }
  }

  // Handle change password
  const handleChangePassword = async (userId, passwordData) => {
    try {
      const result = await userManagementService.updatePassword(userId, passwordData);

      if (result.success) {

        toast.success(`Password changed successfully`)
      } else {
        toast.error(`Failed to change password: ${result.message}`);
      }
    } catch (error) {
      console.error("Error changing password:", error)
      toast.error(`Failed to change password: ${error.message}`)
    }
  }

  // Generate pagination buttons
  const renderPaginationButtons = () => {
    const buttons = []

    // Calculate range of pages to show
    let startPage = Math.max(1, currentPage - 2)
    const endPage = Math.min(totalPages, startPage + 4)

    // Adjust start page if we're near the end
    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4)
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <Button
          key={i}
          variant={currentPage === i ? "default" : "outline"}
          size="icon"
          className="h-8 w-8"
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Button>,
      )
    }

    return buttons
  }

  if (loading) {
    return (
      <div className="p-6 bg-gray-50">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50">
      <h1 className="text-3xl font-bold text-gray-800 text-center">User Management</h1>

      <div className="flex justify-between items-center mb-6">
        <Button onClick={addUser} className="bg-yellow-400 hover:bg-yellow-500 text-black">
          <UserPlus className="mr-2 h-4 w-4" />
          Add User
        </Button>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Search:</span>
          <div className="relative">
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64 pl-2 pr-8 py-1 border border-gray-300 rounded"
              placeholder="Search users..."
            />
            <Search className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>

          <Button onClick={exportToCSV} variant="outline" className="ml-2">
            CSV
          </Button>

          <Button onClick={exportToPDF} variant="outline" className="ml-2">
            PDF
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-md shadow overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px] cursor-pointer" onClick={() => requestSort("profileImage")}>
                Profile
                <ChevronDown className="inline-block ml-1 h-4 w-4" />
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("name")}>
                Name
                <ChevronDown className="inline-block ml-1 h-4 w-4" />
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("email")}>
                Email
                <ChevronDown className="inline-block ml-1 h-4 w-4" />
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("phone")}>
                Phone
                <ChevronDown className="inline-block ml-1 h-4 w-4" />
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("role")}>
                Role
                <ChevronDown className="inline-block ml-1 h-4 w-4" />
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("lastLogin")}>
                Last Login
                <ChevronDown className="inline-block ml-1 h-4 w-4" />
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("mailStatus")}>
                Mail Status
                <ChevronDown className="inline-block ml-1 h-4 w-4" />
              </TableHead>
              <TableHead>
                Actions
                <ChevronDown className="inline-block ml-1 h-4 w-4" />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentEntries.length > 0 ? (
              currentEntries.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Avatar className="h-12 w-12">
                      <AvatarImage
                        src={user?.profileImage?.trim() ? user.profileImage : noprofile}
                        alt={user?.name || 'User'}
                      /> {/* <AvatarFallback>
                        {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                      </AvatarFallback> */}
                    </Avatar>
                  </TableCell>
                  <TableCell>{user.name || 'N/A'}</TableCell>
                  <TableCell>{user.email || 'N/A'}</TableCell>
                  <TableCell>{user.phone || 'N/A'}</TableCell>
                  <TableCell>{user.role || 'N/A'}</TableCell>
                  <TableCell>{user.lastLogin || 'Never'}</TableCell>
                  <TableCell className="relative">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className={`h-8 px-3 text-xs relative z-10 ${user.mailStatus === 'Enabled'
                              ? 'text-green-700 border-green-200 bg-green-50 hover:bg-green-100'
                              : 'text-red-700 border-red-200 bg-red-50 hover:bg-red-100'
                            }`}
                        >
                          {user.mailStatus || 'Unknown'}
                          <ChevronDown className="ml-1 h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="z-50 min-w-[120px] bg-white border border-gray-200 shadow-lg rounded-md p-1"
                        sideOffset={4}
                      >
                        <DropdownMenuItem
                          onClick={() => handleEmailStatusChange(user.id, 'Enabled')}
                          className="text-green-700 hover:text-green-800 hover:bg-green-50 focus:text-green-800 focus:bg-green-50 cursor-pointer px-2 py-1.5 text-sm rounded-sm transition-colors"
                        >
                          <span className="flex items-center">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                            Enabled
                          </span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleEmailStatusChange(user.id, 'Disabled')}
                          className="text-red-700 hover:text-red-800 hover:bg-red-50 focus:text-red-800 focus:bg-red-50 cursor-pointer px-2 py-1.5 text-sm rounded-sm transition-colors"
                        >
                          <span className="flex items-center">
                            <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                            Disabled
                          </span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-blue-600"
                        onClick={() => editUser(user.id)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-amber-500"
                        onClick={() => changePassword(user.id)}
                      >
                        <Key className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600"
                        onClick={() => confirmDeleteUser(user.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-4">
                  {searchTerm ? 'No users found matching your search' : 'No users found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex justify-between items-center mt-4 text-sm">
        <div>
          Showing {filteredUsers.length > 0 ? indexOfFirstEntry + 1 : 0} to{" "}
          {Math.min(indexOfLastEntry, sortedUsers.length)} of {sortedUsers.length} entries
        </div>

        <div className="flex items-center gap-2">
          <select
            value={entriesPerPage}
            onChange={(e) => {
              setEntriesPerPage(Number(e.target.value))
              setCurrentPage(1)
            }}
            className="border border-gray-300 rounded px-2 py-1"
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </select>
          <span>entries per page</span>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {renderPaginationButtons()}

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Modals */}
      <AddUserModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        onAddUser={handleAddUser}
        roles={roles}
      />

      <EditUserModal
        isOpen={isEditUserModalOpen}
        onClose={() => setIsEditUserModalOpen(false)}
        user={selectedUser}
        onUpdateUser={handleUpdateUser}
        roles={roles}
      />

      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
        userId={selectedUser?.id}
        onChangePassword={handleChangePassword}
      />

      <DeleteUserModal
        isOpen={isDeleteDialogOpen}
        onCancel={() => setIsDeleteDialogOpen(false)}
        onConfirm={deleteUser}
        user={selectedUser}
      />
    </div>
  )
}